import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  CreateFineParams,
  createFine,
  getEmployeeFineTotals,
  getEmployeeInsuranceIds,
} from '../services/profile-view-lookups.service';

export const useGetEmployeeInsuranceIds = (userId?: string) =>
  useGlobalQuery({
    queryKey: ['hr-employee-insurance', userId],
    queryFn: () => getEmployeeInsuranceIds(userId as string),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });

export const useGetEmployeeFineTotals = (
  userId?: string,
  periodStart?: string,
  periodEnd?: string
) =>
  useGlobalQuery({
    queryKey: ['hr-employee-fines', userId, periodStart, periodEnd],
    queryFn: () => getEmployeeFineTotals(userId as string, periodStart, periodEnd),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });

/** Issues an ad-hoc lunch/non-compliance fine (legacy `HrFineComponent`), gated on
 * `DIRECTORY.CREATE_FINE`. Invalidates the fine totals shown on the employee's profile overview. */
export const useCreateFine = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: CreateFineParams) => createFine(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-employee-fines'] });
      toast({ title: t('FINE_CREATED') });
    },
  });
};
